import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import Step_Welcome, {
  Step_Consent, Step_Rejected, Step_AgeRange, Step_OutOfRange, Step_Already12m,
} from './BilanSteps';
import { QuestionnaireRunner } from './BilanWidgets';
import { build1825Steps } from './BilanQuestionnaire1825';
import { build4550Steps } from './BilanQuestionnaire4550';
import { build6065Steps } from './BilanQuestionnaire6065';
import { build7075Steps } from './BilanQuestionnaire7075';
import { logger } from '../../utils/logger';
import { API_URL as API } from '../../utils/api';

const BUILDERS = {
  '18-25': build1825Steps,
  '45-50': build4550Steps,
  '60-65': build6065Steps,
  '70-75': build7075Steps,
};

const BG = 'min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50/60';

// Local persistence — keeps the user's progress if they refresh the page.
// Stored per slug. Auto-expires after 3 hours (matches the 2h30 backend TTL + margin).
//
// SECURITY NOTE — Audit code-review (avr 2026) :
//   localStorage est plat-list pour ce flux car les réponses sont 100%
//   pseudonymisées (option C choisie par le client) : aucun nom, aucune date
//   de naissance exacte, aucun identifiant patient, aucun token. Uniquement
//   des choix radios/checkboxes, compteurs et sliders. Les seules valeurs
//   "sensibles" théoriques (PHQ-2, GAD-2, AUDIT-C) sont des scores en clair
//   mais sans aucun rattachement nominatif. La donnée brute n'a pas de valeur
//   ré-identifiante seule. Pas de migration nécessaire vers httpOnly cookies.
const DRAFT_KEY = (slug) => `bilan_draft::${slug}`;
const DRAFT_TTL_MS = 3 * 60 * 60 * 1000;

const loadDraft = (slug) => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.saved_at || Date.now() - parsed.saved_at > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY(slug));
      return null;
    }
    return parsed;
  } catch (err) {
    // Corrupted JSON or quota issue — discard the draft instead of crashing
    if (typeof console !== 'undefined') logger.warn('[Bilan] loadDraft failed:', err?.message);
    return null;
  }
};

const saveDraft = (slug, status, answers, questionnaireIdx = 0) => {
  try {
    // Never persist the final "done" state (code is sensitive + short-lived)
    if (status === 'done' || status === 'submitting' || status === 'loading') return;
    localStorage.setItem(
      DRAFT_KEY(slug),
      JSON.stringify({ status, answers, questionnaireIdx, saved_at: Date.now() }),
    );
  } catch (err) {
    // localStorage full or unavailable (private mode Safari) — silently degrade
    if (typeof console !== 'undefined') logger.warn('[Bilan] saveDraft failed:', err?.message);
  }
};

const clearDraft = (slug) => {
  try {
    localStorage.removeItem(DRAFT_KEY(slug));
  } catch (err) {
    if (typeof console !== 'undefined') logger.warn('[Bilan] clearDraft failed:', err?.message);
  }
};

const INITIAL_ANSWERS = {
  age_range: '', sex: '', already_done_12m: '',
  motifs: [], atcd_personnels: [], atcd_familiaux: [], traitements: [], allergies: [],
  tabac: '', tabac_cig_j: null, tabac_years: null, tabac_wants_stop: '',
  alcool_audit_q1: null, alcool_audit_q2: null, alcool_audit_q3: null,
  activite_physique_min_sem: null,
  sommeil_qualite: '',
  phq2_q1: null, phq2_q2: null, gad2_q1: null, gad2_q2: null,
  vaccin_dtp: '', vaccin_grippe: '', vaccin_covid: '',
  depistage_mammo: '', depistage_fcu: '', depistage_hemoccult: '', depistage_psa: '',
  red_flags: {},
  extra_responses: {},
};

export default function BilanPatientPage() {
  const { slug } = useParams();
  const [status, setStatus] = useState('loading');
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [questionnaireIdx, setQuestionnaireIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // 1. Check the slug + load config, and restore any previous draft from localStorage
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [check, cfg] = await Promise.all([
          axios.get(`${API}/api/bilan/check/${encodeURIComponent(slug)}`),
          axios.get(`${API}/api/bilan/config`),
        ]);
        if (!alive) return;
        if (!check.data?.valid) { setStatus('invalid'); return; }
        setConfig(cfg.data);
        // Restore previous draft if any (same slug, still fresh)
        const draft = loadDraft(slug);
        if (draft && draft.answers && draft.status) {
          setAnswers({ ...INITIAL_ANSWERS, ...draft.answers });
          setQuestionnaireIdx(Number.isInteger(draft.questionnaireIdx) ? draft.questionnaireIdx : 0);
          setStatus(draft.status);
        } else {
          setStatus('welcome');
        }
      } catch (e) {
        if (!alive) return;
        setStatus('invalid');
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // 2. Auto-save draft whenever status/answers/idx change (skip loading/invalid/done)
  useEffect(() => {
    if (!slug) return;
    if (status === 'loading' || status === 'invalid' || status === 'done' || status === 'submitting') return;
    saveDraft(slug, status, answers, questionnaireIdx);
  }, [slug, status, answers, questionnaireIdx]);

  const update = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  // Submit
  const submit = async () => {
    setStatus('submitting');
    setError('');
    try {
      const payload = {
        ...answers,
        doctor_slug: slug,
        consent_given: true,
        consent_timestamp: new Date().toISOString(),
      };
      const { data } = await axios.post(`${API}/api/bilan/submit`, payload);
      setResult(data);
      clearDraft(slug); // success → wipe local draft
      setStatus('done');
    } catch (e) {
      setStatus('questionnaire');
      setError(e.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    }
  };

  // --- Render
  if (status === 'loading') {
    return (
      <div className={`${BG} flex items-center justify-center`}>
        <Loader2 className="w-7 h-7 animate-spin text-teal-500" />
      </div>
    );
  }
  if (status === 'invalid') {
    return (
      <div className={`${BG} flex items-center justify-center px-4`}>
        <div className="max-w-md text-center">
          <div className="inline-block bg-rose-100 rounded-3xl p-5 mb-4"><AlertTriangle className="w-9 h-9 text-rose-500" /></div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Lien invalide</h2>
          <p className="text-sm text-slate-500">Ce lien ne correspond à aucun cabinet actif. Vérifiez le QR code auprès de votre médecin.</p>
        </div>
      </div>
    );
  }
  if (status === 'done') {
    return (
      <div className={BG}>
        <DoneScreen code={result?.code} expiresAt={result?.expires_at} />
      </div>
    );
  }

  return (
    <div className={BG} data-testid="bilan-patient-page">
      {status === 'welcome' && <Step_Welcome onStart={() => setStatus('consent')} />}
      {status === 'consent' && <Step_Consent onAccept={() => setStatus('age')} onReject={() => setStatus('rejected')} />}
      {status === 'rejected' && <Step_Rejected onBack={() => setStatus('welcome')} />}
      {status === 'age' && (
        <Step_AgeRange
          onPick={(v) => { update({ age_range: v }); setStatus('already'); }}
          onOut={() => setStatus('out')}
        />
      )}
      {status === 'out' && <Step_OutOfRange onBack={() => setStatus('welcome')} />}
      {status === 'already' && (
        <Step_Already12m
          onAnswer={(v) => {
            update({ already_done_12m: v });
            setStatus('questionnaire');
          }}
          onBack={() => setStatus('age')}
        />
      )}
      {status === 'questionnaire' && config && (
        <>
          {error && <div className="max-w-lg mx-auto mt-4 px-4"><div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700">{error}</div></div>}
          <QuestionnaireRunner
            buildSteps={BUILDERS[answers.age_range] || build4550Steps}
            config={config} answers={answers} update={update}
            initialIdx={questionnaireIdx}
            onIdxChange={setQuestionnaireIdx}
            onBack={() => { setQuestionnaireIdx(0); setStatus('already'); }}
            onDone={submit}
          />
        </>
      )}
      {status === 'submitting' && (
        <div className="max-w-lg mx-auto pt-24 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto mb-3" />
          <div className="text-slate-600 text-sm">Transmission sécurisée en cours...</div>
        </div>
      )}
    </div>
  );
}

function DoneScreen({ code, expiresAt }) {
  const [elapsed, setElapsed] = useState(Date.now());
  useEffect(() => {
    // Auto-wipe screen after 5 min inactivity (safety)
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => setElapsed(Date.now() + 999999999), 5 * 60 * 1000); };
    const events = ['mousemove', 'touchstart', 'keydown'];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(t);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  const expired = elapsed > Date.now() + 999999998;
  if (expired) {
    return (
      <div className="max-w-md mx-auto pt-24 text-center px-4">
        <div className="inline-block bg-slate-100 rounded-3xl p-5 mb-4"><Clock className="w-9 h-9 text-slate-400" /></div>
        <h2 className="text-lg font-bold text-slate-600 mb-2">Écran expiré</h2>
        <p className="text-sm text-slate-500">Par sécurité, votre code a été masqué. Notez-le ou rafraîchissez la page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pt-10 px-4 pb-16 text-center relative overflow-hidden" data-testid="bilan-done">
      {/* ─── Confetti CSS (20 particules, léger, disparaît après 3 s) ─── */}
      {/* eslint-disable-next-line react/no-array-index-key */}
      <div className="confetti-container pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <span key={`confetti-${i}`} className="confetti-piece" style={{
            left: `${Math.random() * 100}%`,
            background: ['#2dd4bf', '#38bdf8', '#818cf8', '#f472b6', '#facc15'][i % 5],
            animationDelay: `${Math.random() * 0.6}s`,
            animationDuration: `${2 + Math.random() * 1.2}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }} />
        ))}
      </div>

      {/* ─── Animated success check ─── */}
      <div className="relative z-10 inline-flex items-center justify-center mb-5">
        <div className="absolute w-28 h-28 rounded-full bg-emerald-100/70 animate-ping-slow" />
        <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl">
          <svg viewBox="0 0 52 52" className="w-12 h-12">
            <circle cx="26" cy="26" r="25" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            <path className="check-draw" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
              d="M14 27 L23 36 L39 18" strokeDasharray="60" strokeDashoffset="60" />
          </svg>
        </div>
      </div>

      <h1 className="relative z-10 text-3xl font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Bravo, c'est fait ! 🎉
      </h1>
      <p className="relative z-10 text-sm text-slate-500 mb-6">Votre bilan est prêt pour votre médecin.</p>

      {/* ─── Code card (hero element) ─── */}
      <div className="relative z-10 bg-white rounded-3xl border-2 border-teal-200 p-6 shadow-xl">
        <div className="text-[11px] uppercase tracking-[0.2em] text-teal-600 font-semibold mb-2">Votre code</div>
        <div className="text-5xl font-bold bg-gradient-to-br from-teal-600 to-sky-600 bg-clip-text text-transparent tracking-wider"
          style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="bilan-code">
          {code || '—'}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-600 font-medium">📣 Dites ce code à votre médecin</p>
          <p className="text-[11px] text-slate-400 mt-1">Il l'utilisera pour récupérer vos réponses.</p>
        </div>
      </div>

      <p className="relative z-10 text-[11px] text-slate-400 mt-5 inline-flex items-center gap-1.5 justify-center">
        <Clock className="w-3 h-3" />
        Votre bilan sera supprimé à {expiresAt ? new Date(expiresAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'dans 2h30'}
      </p>

      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .confetti-piece {
          position: absolute;
          top: -20px;
          width: 8px;
          height: 14px;
          border-radius: 2px;
          animation: confettiFall linear forwards;
        }
        @keyframes checkDraw {
          from { stroke-dashoffset: 60; }
          to { stroke-dashoffset: 0; }
        }
        .check-draw { animation: checkDraw 500ms ease-out 300ms forwards; }
        @keyframes pingSlow {
          0% { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-ping-slow { animation: pingSlow 1.8s ease-out infinite; }
      `}</style>
    </div>
  );
}
